import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import api, { formatError } from "@/lib/api";
import HolidayWaitlist from "@/components/admin/HolidayWaitlist";
import HolidaySettings from "@/components/admin/HolidaySettings";

const inp = "bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-sm";

export default function PromoCodes() {
  const [codes, setCodes] = useState(null);
  const load = () => api.get("/admin/promo-codes").then(({ data }) => setCodes(data.codes)).catch(() => setCodes([]));
  useEffect(() => { load(); }, []);
  const toggle = async (c) => {
    try { await api.post(`/admin/promo-codes/${c.id}/active`, { active: !c.active }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4" data-testid="admin-promos">
      <HolidaySettings />
      <HolidayWaitlist />
      <NewCode onDone={load} />
      {!codes ? <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
        : !codes.length ? <p className="text-slate-400 text-center py-6" data-testid="admin-promos-empty">No promo codes yet.</p>
        : codes.map((c) => (
          <div key={c.id} className={`bg-slate-800 rounded-xl border-2 border-slate-700 px-4 py-3 flex flex-wrap items-center gap-3 text-sm ${c.active ? "" : "opacity-60"}`} data-testid={`admin-promo-${c.code}`}>
            <span className="font-mono font-bold text-base text-yellow-300">{c.code}</span>
            <span className="font-semibold">{c.label}</span>
            <span className="text-slate-400">Ends: {c.ends_at ? new Date(c.ends_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "no end date"}</span>
            <span className="text-slate-400" data-testid={`admin-promo-uses-${c.code}`}>Used {c.uses}{c.max_uses ? ` / ${c.max_uses}` : ""}</span>
            <button onClick={() => toggle(c)} data-testid={`admin-promo-toggle-${c.code}`}
              className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold border-2 ${c.active ? "border-red-400 text-red-200" : "border-green-400 text-green-200"}`}>
              {c.active ? "Switch off" : "Switch on"}
            </button>
          </div>
        ))}
    </div>
  );
}

function NewCode({ onDone }) {
  const blank = { code: "", kind: "percent", value: "", ends_on: "", max_uses: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "code" ? e.target.value.toUpperCase() : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/promo-codes", { ...f, value: Number(f.value), max_uses: Number(f.max_uses || 0) });
      toast.success(`Promo code ${data.code.code} created`);
      setF(blank); onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="bg-slate-800 rounded-2xl border-2 border-yellow-300/40 p-4 flex flex-wrap items-end gap-3" data-testid="admin-promo-form">
      <Field label="Code"><input required value={f.code} onChange={set("code")} placeholder="NOEL20" className={`${inp} w-32 uppercase`} data-testid="admin-promo-code" /></Field>
      <Field label="Type">
        <select value={f.kind} onChange={set("kind")} className={inp} data-testid="admin-promo-kind">
          <option value="percent">Percentage (%)</option>
          <option value="fixed">Fixed amount (Rs per child)</option>
        </select>
      </Field>
      <Field label={f.kind === "percent" ? "% off" : "Rs off"}><input required type="number" min={1} value={f.value} onChange={set("value")} className={`${inp} w-24`} data-testid="admin-promo-value" /></Field>
      <Field label="End date (optional)"><input type="date" value={f.ends_on} onChange={set("ends_on")} className={inp} data-testid="admin-promo-ends" /></Field>
      <Field label="Max uses (0 = no limit)"><input type="number" min={0} value={f.max_uses} onChange={set("max_uses")} className={`${inp} w-28`} data-testid="admin-promo-max" /></Field>
      <button disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-yellow-300 text-slate-900 px-4 py-2 text-sm font-bold" data-testid="admin-promo-create">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create code
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return <label className="flex flex-col gap-1 text-xs text-slate-400">{label}{children}</label>;
}
